import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import ModalInfinityList from "components/ModalInfinityList";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput, mq } from "utils";

import { useCoWorkers } from "clients/apis";
import Button from "components/Button";

const unregisteredItem = {
  id: null,
  title: strings.unregistered,
  name: strings.unregistered,
  text: strings.unregistered,
};

let timer: ReturnType<typeof setTimeout>;

type CoWorker = { id: number; name?: string; title?: string };

type Props = {
  value: Array<CoWorker>;
  onApply: (categories: Array<CoWorker>) => void;
};

const CoWorkerMultiSelector: React.FC<Props> = (props) => {
  const { value, onApply } = props;

  const [name, setName] = useState("");
  const {
    refetch,
    isError,
    error: customerError,
    isRefetching,
    isLoading: customersLoading,
    data: customers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCoWorkers({ name });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onClosePicker = () => {
    setName("");
    hide();
  };

  const [valueState, setValueState] = useState<Array<CoWorker>>([]);

  useEffect(() => {
    setValueState(value);
  }, [value]);

  const _onApply = () => {
    onApply(valueState);
    hide();
  };

  const _renderItem = (items: Array<any>) => {
    return items.map((co: CoWorker, key: number) => {
      const itemIndex = valueState.findIndex((e) => e.id === co.id);
      const active = itemIndex > -1;
      const _onPress = () => {
        active
          ? setValueState(valueState?.filter((e) => e.id !== co.id))
          : setValueState((prev) => [...prev, co]);
      };
      return (
        <TouchableOpacity
          onPress={_onPress}
          key={String(key)}
          style={{
            ...styles.item,
            backgroundColor: active ? colors.theme : colors.white,
          }}
        >
          <Text color={active ? colors.white : colors.text.normal}>
            {co.name}
          </Text>
          {active && (
            <View style={{ position: "absolute", left: 10 }}>
              <Icon type="antDesign" name={"close"} color={colors.white} />
            </View>
          )}
        </TouchableOpacity>
      );
    });
  };

  const _renderList = () => {
    if (customersLoading || typing) {
      return <Loading style={{ marginVertical: mq(20) }} />;
    }

    if (isError) {
      return (
        <Error error={customerError} reset={refetch} loading={isRefetching} />
      );
    }

    if (customers?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_item_found} />;
    }

    return (
      <>
        {_renderItem([unregisteredItem])}
        <AnimatedInfinityList
          onRefresh={refetch}
          responseData={customers}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          renderItemList={_renderItem}
        />

        <Button
          square={45}
          absolute
          bottom={10}
          left={10}
          onPress={_onApply}
          icon={{ type: "antDesign", name: "check" }}
        />
      </>
    );
  };

  return (
    <>
      <TouchableOpacity
        onPress={show}
        activeOpacity={sizes.tapOpacity}
        style={{ ...styles.inputContainer }}
      >
        <View style={styles.iconContainer}>
          <Icon name="chevron-down" />
        </View>
        <View style={styles.textContainer}>
          <Text
            color={valueState ? colors.textColorDefault : colors.placeholder}
          >
            {strings.pick_co_worker}
          </Text>
        </View>

        <View style={styles.iconContainer}>
          <Icon name="users" />
        </View>
      </TouchableOpacity>

      <ModalInfinityList close={_onClosePicker} visible={modal}>
        <View style={styles.listSearchContainer}>
          <Input
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) =>
              liveSearchInput(
                inputName,
                name,
                setName,
                timer,
                startTyping,
                stopTyping
              )
            }
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
};

export default React.memo(CoWorkerMultiSelector);
